import React, { useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { TrialLocationView } from '@data/sdk/match';
import { Namespace } from '@data/utils/enums';
import { AntdCol, AntdRow, AntdSkeleton, AntdTypography } from '@ui/components';
import { Map } from './components';

type TrialLocationsProps = {
  isLoading: boolean;
  locations: TrialLocationView[] | undefined;
};

const { Text } = AntdTypography;
const MAX_LOCATIONS_TO_SHOW = 3;

export const TrialLocations = ({ isLoading, locations }: TrialLocationsProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const [showAllLocations, setShowAllLocations] = useState(false);

  return (
    <AntdRow className="mt-4" gutter={16}>
      <AntdCol span={24} lg={16} className="flex flex-col">
        {isLoading || !locations ? (
          <AntdSkeleton.Input className="block h-72 w-full" />
        ) : (
          <Map locations={locations} />
        )}
      </AntdCol>
      <AntdCol span={24} lg={8} className="flex flex-col">
        <Text className="text-base-500">{t('trial_details_page.locations')}</Text>
        {locations ? (
          <>
            <ul
              className={cx('pl-4 overflow-y-auto', {
                'overflow-y-hidden': !showAllLocations,
                'h-60': showAllLocations,
              })}
            >
              {locations.map((location, index) =>
                showAllLocations || index < MAX_LOCATIONS_TO_SHOW ? (
                  <li key={location.id}>
                    <Text>
                      {location.facility} {location.city}, {location.country} {location.zip}
                    </Text>
                  </li>
                ) : null,
              )}
            </ul>
            {locations?.length > MAX_LOCATIONS_TO_SHOW && (
              <button
                className="text-start pl-0 border-none bg-transparent cursor-pointer text-primary-600 hover:text-primary-700"
                onClick={() => setShowAllLocations(!showAllLocations)}
              >
                {showAllLocations
                  ? t('trial_details_page.show_less')
                  : t('trial_details_page.show_more', {
                      count: (locations?.length ?? 0) - MAX_LOCATIONS_TO_SHOW,
                    })}
              </button>
            )}
          </>
        ) : (
          <AntdSkeleton.Input className="block h-40 w-full" />
        )}
      </AntdCol>
    </AntdRow>
  );
};
